// © Microsoft Corporation. All rights reserved.

import { WebComponentExperience } from "@msnews/experiences-web-component";
import { attr } from "@microsoft/fast-element";
import { ExperienceIds } from "@msnews/experiences-constants";
import { PublisherCarouselConfig } from "./PublisherCarousel.config";
import { PublisherCarouselStrings } from "./PublisherCarousel.strings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { PublisherCarouselData, PublisherCarouselTheme } from "./PublisherCarouselData";
import { MsftStripe } from "@fast/components";
import { SocialService } from "@msnews/social-data-service";
import { isStateAllowed, mapPublisherContentToCarouselData } from "./PublisherCarouselDataMapper";
import { PublisherCarouselThemeStyles } from "./PublisherCarousel.styles";
import { mapPublisherCarouselTelemetry, PublisherCarouselTelemetryContext, setupHybridTelemetry } from "./PublisherCarousel.telemetry";
import { TelemetryObject } from "@msnews/telemetry-core";

/** PublisherCarousel webcomponent experience class definition */
export class PublisherCarousel extends WebComponentExperience<PublisherCarouselConfig, PublisherCarouselStrings> {
    /**
     * Run on initialization after configs are populated but before the Shadow DOM is filled out with the templates.
     */
    experienceConnected() {
        this.shouldPreventRender = !isStateAllowed();

        this.combineStrings();
        this.isHybrid = this.hybridTelemetry && setupHybridTelemetry(this.hybridTelemetry);
    }

    /**
     * Returns the experience type
     */
    getExperienceType(): ExperienceIds {
        return ExperienceIds.publisherCarousel;
    }

    /**
     * Returns the experience type
     */
    shadowDomPopulated() {
        if (this.shouldPreventRender) { return; }
        if (this.publisherProfileId !== "cid-1392eefe780000ee") { return; }  // render only for Business Insider for now

        // prepare props
        this.theme = this.theme || PublisherCarouselTheme.default;

        // fetch content
        this.fetchContentData();
    }

    /**
     * Listen attribute change in order to init stripe component
     */
    attributeChangedCallback(attrName: string, oldVal: any, newVal: any) {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        this.initStripe();
    }

    async fetchContentData() {
        if (this.publisherProfileId) {
            const publisherCardImageSize = PublisherCarouselThemeStyles[this.theme]?.imageSize || { width: 220, height: 124 };
            const publisherContentDataArray = (await SocialService.getPublisherFeed(this.publisherProfileId, "en-us"))?.items;

            this.rootTelemetryObject = this.rootTelemetryObject || new TelemetryObject({name: "PublisherCarouselContainer"});
            this.telemetryContext = publisherContentDataArray && this.rootTelemetryObject && mapPublisherCarouselTelemetry(this.rootTelemetryObject, this.isHybrid);

            this.carouselData = publisherContentDataArray && mapPublisherContentToCarouselData(publisherContentDataArray, publisherCardImageSize, this.telemetryContext?.carousel, this.strings);
        }
    }

    initStripe(): void {
        setTimeout(() => { this.stripe?.connectedCallback(); }, 100);
    }

    combineStrings() {
        this.strings = {
            ...(this.config as PublisherCarouselStrings),
            ...this.strings
        };
    }

    /**
     * Props
     */
    @attr public backgroundSheetDisabled: boolean;
    @attr public publisherProfileId: string;
    @attr public locale: string;
    @attr public theme: PublisherCarouselTheme;
    @attr public hybridTelemetry: {handleTelemetryCall, handlePartialPageImpressionCall, handleUpdateTemplValueCallback};

    // Telemetry
    @attr public rootTelemetryObject: TelemetryObjectBase;
    @attr public telemetryContext: PublisherCarouselTelemetryContext;

    /**
     * Instance Members
     */
    @attr isHybrid: boolean;
    @attr public shouldPreventRender: boolean;
    @attr public carouselData: PublisherCarouselData;
    stripe: MsftStripe;
}